"use client";

import { useStyles } from "./styles";
import { MODELS } from "../../constants";
import AutoModelIcon from "@/assets/models/auto-model.webp";
import NSFW_FastIcon from "@/assets/models/nsfw-fast.webp";
import Imagen4Icon from "@/assets/models/imagen-4.webp";
import NSFW_Icon from "@/assets/models/nsfw.webp";
import GPT_Icon from "@/assets/models/gpt.webp";
import FluxProIcon from "@/assets/models/flux-pro.webp";
import Imagen4UltraIcon from "@/assets/models/imagen-4-ultra.webp";
import Grok2Icon from "@/assets/models/grok-2.webp";
import FluxKontextIcon from "@/assets/models/flux-kontext.webp";
import IdyllicBaseIcon from "@/assets/models/idyllic-base.webp";
import IdyllicHDIcon from "@/assets/models/idyllic-hd.webp";
import IdyllicHDR_PlusIcon from "@/assets/models/idyllic-hdr-plus.webp";

function getModelDescription(model: string) {
  switch (model) {
    case "auto":
      return "Automatically chooses the best model for your specific prompt, optimizing for quality and relevance.";
    case "nsfw-fast":
      return "Quick generation of adult content with good quality and lower latency.";
    case "imagen-4":
      return "Google's powerful image generation model known for photorealistic results.";
    case "nsfw":
      return "Model optimized for generating adult content with realistic details.";
    case "gpt":
      return "OpenAI's advanced image generation model that excels at creating detailed images.";
    case "flux-pro":
      return "Advanced model specialized in creating detailed and artistic visuals with enhanced features.";
    case "imagen-4-ultra":
      return "Premium version of Imagen with enhanced detail and quality.";
    case "grok-2":
      return "xAI's innovative image model that specializes in creative and artistic interpretations.";
    case "flux-kontext":
      return "Context-aware image generation with improved understanding of complex prompts.";
    case "hd-turbo":
      return "Idyllic's base model, designed for quick and efficient image generation.";
    case "hd":
      return "High-definition version of Idyllic, focusing on detailed and vibrant images.";
    case "hd+":
      return "Idyllic's HDR Plus model, offering enhanced dynamic range and color accuracy for stunning visuals.";
    default:
      return "Automatically chooses the best model for your specific prompt, optimizing for quality and relevance.";
  }
}

function getModelThumbnail(model: string) {
  switch (model) {
    case "auto":
      return AutoModelIcon.src;
    case "nsfw-fast":
      return NSFW_FastIcon.src;
    case "imagen-4":
      return Imagen4Icon.src;
    case "nsfw":
      return NSFW_Icon.src;
    case "gpt":
      return GPT_Icon.src;
    case "flux-pro":
      return FluxProIcon.src;
    case "imagen-4-ultra":
      return Imagen4UltraIcon.src;
    case "grok-2":
      return Grok2Icon.src;
    case "flux-kontext":
      return FluxKontextIcon.src;
    case "hd-turbo":
      return IdyllicBaseIcon.src;
    case "hd":
      return IdyllicHDIcon.src;
    case "hd+":
      return IdyllicHDR_PlusIcon.src;
    default:
      return AutoModelIcon.src;
  }
}

interface ModelPreviewProps {
  aspectRatio: string;
  selectedModel: string;
}

const ModelPreview: React.FC<ModelPreviewProps> = ({
  aspectRatio,
  selectedModel,
}) => {
  const { classes } = useStyles();
  const thumbnail = getModelThumbnail(selectedModel);
  const label = MODELS.find((m) => m.name === selectedModel)?.label || "Auto";
  const description = getModelDescription(selectedModel);
  return (
    <div className={classes.container}>
      <img
        src={thumbnail}
        alt={`${selectedModel} model`}
        className={classes.thumbnail}
        style={{ aspectRatio }}
      />
      <p className={classes.description}>
        <strong>{label}:</strong> {description}
      </p>
    </div>
  );
};

export default ModelPreview;
